import { LitElement, TemplateResult, html, unsafeCSS } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';

import styles from './Switch.scss?inline';

export type SwitchSize = 'sm' | 'md' | 'lg';

export interface SwitchChangeDetail {
    checked: boolean;
    name: string;
    value: string;
}

@customElement('app-switch')
export class SwitchComponent extends LitElement {
    static styles = unsafeCSS(styles);

    @property({ type: Boolean, reflect: true }) checked = false;
    @property({ type: Boolean, reflect: true }) disabled = false;
    @property({ type: Boolean, reflect: true }) required = false;

    @property({ type: String }) name = '';
    @property({ type: String }) value = 'on';
    @property({ type: String }) label = '';
    @property({ type: String }) description = '';
    @property({ type: String, attribute: 'aria-label' }) ariaLabel = 'Interruptor';

    @property({ type: String, reflect: true }) size: SwitchSize = 'md';

    private get finalAriaLabel(): string {
        return this.label || this.ariaLabel || 'Interruptor';
    }

    private toggle(): void {
        if (this.disabled) return;
        this.checked = !this.checked;
        this.emitEvents();
    }

    private onKeyDown(e: KeyboardEvent): void {
        if (this.disabled) return;
        if (e.key === ' ' || e.key === 'Enter') {
            e.preventDefault();
            this.toggle();
        }
    }

    private onTextClick(e: MouseEvent): void {
        e.preventDefault();
        e.stopPropagation();
        this.toggle();
    }

    private emitEvents(): void {
        const detail: SwitchChangeDetail = {
            checked: this.checked,
            name: this.name,
            value: this.value,
        };

        this.dispatchEvent(
            new Event('change', {
                bubbles: true,
                composed: true,
            })
        );

        this.dispatchEvent(
            new CustomEvent<SwitchChangeDetail>('switch-change', {
                detail,
                bubbles: true,
                composed: true,
            })
        );
    }

    render(): TemplateResult<1> {
        const ariaChecked: 'true' | 'false' = this.checked ? 'true' : 'false';
        const ariaDisabled: 'true' | 'false' = this.disabled ? 'true' : 'false';

        const hostClasses = {
            checked: this.checked,
            disabled: this.disabled,
            required: this.required,
        };

        const descriptionFinal = this.description ? this.description.trim() : '';

        return html`
			<label class=${classMap(hostClasses)} @click=${(e: MouseEvent) => e.preventDefault()}>
				<button
					class="switch"
					type="button"
					role="switch"
					aria-label=${this.finalAriaLabel}
                    aria-checked=${ariaChecked}
                    aria-disabled=${ariaDisabled}
					?disabled=${this.disabled}
					@click=${this.toggle}
					@keydown=${this.onKeyDown}
				>
					<span class="switch-track">
						<span class="switch-thumb"></span>
					</span>
				</button>

				${this.label || this.description
                ? html`
                            <span class="text-content" @click=${this.onTextClick}>
								${this.label ? html`<span class="label">${this.label}</span>` : null}
								${descriptionFinal ? html`<span class="description">${descriptionFinal}</span>` : null}
							</span>
						`
                : null}
			</label>
		`;
    }
}

export type SwitchChangeEvent = CustomEvent<SwitchChangeDetail>;